import React, { useEffect, useRef, useState } from 'react';

import { useTheme } from '@emotion/react';
import { Box, Grid, Typography } from '@mui/material';

import { getPersonContactRecords } from 'pages/ContactInformation/utils';
import { getPersonMedicalRecords } from 'pages/MedicalInformation/utils';
import { OutlineButton } from 'shared-components/Buttons/Outline';
import { Container } from 'shared-components/Container';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import PersonalInformationLoader from './skeletonLoader';
import PersonInformationModal from './Modal';
import { useGetStatesQuery } from 'store/api/pkd/endpoints/country';
import { Gender } from 'utils/enum';

export const PersonalInformation = ({
  isPulseSurveyOpenModal,
  setIsPulseSurveyOpenModal,
  toggleIsOptingIn,
}) => {
  const theme = useTheme();
  const { person, auth0User, isPersonLoading, personId } =
    useGetPersonAndAttributes();

  const { states, isStatesLoading } =
  useGetStatesQuery(
    null,
    {
      selectFromResult : ({ data, isLoading }) => ({
        states          : data?.subdivisions,
        isStatesLoading : isLoading,
      }),
    }
  );
  const personInfo = {
    ...getPersonContactRecords(person?.attributes),
    ...getPersonMedicalRecords(person?.attributes, {
      date_of_birth : '',
      gender        : '',
      first_name    : '',
      last_name     : '',
    }),
  };

  const hasOptedInRef = useRef(false);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const toggleEditProfile = () => {
    setIsOpenModal((prev) => {
      const isOpen = !prev;
      setIsPulseSurveyOpenModal(false);
      return isOpen;
    });
  };

  const handleIsOptingIn = () => {
    toggleIsOptingIn?.(hasOptedInRef.current);
    toggleEditProfile();
  };
  
  const getStateNameByCode = (code) => {
    const state = states?.find(state => state.code === code);
    return state ? state.name : '-';
  };
  
  useEffect(() => {
    setIsOpenModal(isPulseSurveyOpenModal);
  }, [isPulseSurveyOpenModal]);
  const { first_name: firstName, last_name: lastName } = personInfo;

  const skeletonLoading = isPersonLoading || isStatesLoading;

  const getGenderDisplay = (genderValue) => {
    const genderKey = Object.keys(Gender).find(key => Gender[key] === genderValue);
    return genderKey;
  };
  const content = skeletonLoading
    ? (
      <PersonalInformationLoader />
    )
    : (
      <Box>
        <Grid
          container
          mt={1}
          mb={4}
          spacing={2}
          direction={{ xs: 'column', sm: 'row' }}>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            First name
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {firstName}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            Last name
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {lastName}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            Email
            </Typography>
            <Typography
              variant='body2' mt={1} sx={{ wordBreak: 'break-all' }}>
              {auth0User?.email ?? '-'}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            Home Phone number
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {personInfo?.home_phone ?? '-'}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            Mobile Phone number
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {personInfo?.phone ?? '-'}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            Patient Date of birth
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {personInfo?.date_of_birth
                ? new Date(personInfo?.date_of_birth).toDateString()
                : '-'}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            Address
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {personInfo?.address ?? '-'}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            City
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {personInfo?.city ?? '-'}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            State
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {getStateNameByCode(personInfo?.state) ?? ''}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            Zip Code
            </Typography>
            <Typography
              variant='body2' mt={1}>
              {personInfo?.postal_code ?? '-'}
            </Typography>
          </Grid>
          <Grid
            item sm={6}>
            <Typography
              variant='pb5' color='primary.grey5'>
            Gender
            </Typography>
            <Typography
              variant='body2' mt={1} textTransform='capitalize'>
              {getGenderDisplay(personInfo?.gender)}
            </Typography>
          </Grid>
        </Grid>
        <OutlineButton
          width='100%'
          fontFamily='Montserrat'
          color={theme.palette.primary.blue1}
          borderColor={theme.palette.primary.blue1}
          text='Edit personal information'
          onClick={toggleEditProfile}
        />
      </Box>
    );

  return (
    <>
      <Container
        maxWidth={{ xl: '800px' }} mb={4}>
        <Typography
          fontFamily='Montserrat'
          fontWeight='700'
          variant='h6'
          mb={4}>
          Personal information
        </Typography>
        {content}
      </Container>
      {isOpenModal && (
        <PersonInformationModal
          isOpenModal={isOpenModal}
          handleClose={toggleEditProfile}
          person={person}
          auth0User={auth0User}
          personId={personId}
          isPulseSurveyOpenModal={isPulseSurveyOpenModal}
          isPersonLoading={isPersonLoading}
          hasOptedInRef={hasOptedInRef}
          handleIsOptingIn={handleIsOptingIn}
          states={states}
        />
      )}
    </>
  );
};
